import { useState, useEffect } from 'react';
import { useNavigate, useParams } from '@tanstack/react-router';
import { motion } from 'motion/react';
import { RiPlayLine, RiCodeLine, RiSaveLine, RiAlertLine } from '@remixicon/react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { SidebarProvider, SidebarInset, SidebarTrigger } from '@/components/ui/sidebar';
import { Separator } from '@/components/ui/separator';
import { AppSidebar } from '@/components/app-sidebar';
import { ModeToggle } from '@/components/mode-toggle';
import { VisualCanvas } from '@/components/query-builder/visual-canvas';
import { DatabaseSwitcher } from '@/components/database-switcher';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { useSession } from '@/lib/auth-client';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
} from '@/components/ui/breadcrumb';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

interface TableSchema {
  name: string;
  fields: Array<{
    name: string;
    type: string;
    isPrimary: boolean;
    isForeign: boolean;
    foreignTable?: string;
    foreignKey?: string;
  }>;
}

export function VisualQueryBuilderPage() {
  const navigate = useNavigate();
  const { id } = useParams({ strict: false });
  const { data: session } = useSession();
  const [schema, setSchema] = useState<TableSchema[]>([]);
  const [sql, setSql] = useState<string>('');
  const [isExecuting, setIsExecuting] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [showExecuteDialog, setShowExecuteDialog] = useState(false);

  useEffect(() => {
    if (!id) {
      toast.error('Please select a database first');
      navigate({ to: '/databases' });
      return;
    }

    if (session?.user?.id) {
      loadSchema();
    }
  }, [id, session?.user?.id]);

  const loadSchema = async () => {
    if (!id) return;

    try {
      const result = await api.getDatabaseSchema(id as string);
      setSchema(result.tables);
    } catch (error: any) {
      console.error('Failed to load schema', error);
      toast.error(error.message || 'Failed to load database schema');
    }
  };

  const executeQuery = async () => {
    if (!sql || !id) return;

    setShowExecuteDialog(false);
    setIsExecuting(true);
    try {
      const result = await api.executeQuery(id as string, sql);
      setResult(result);
      toast.success('Query executed successfully');
    } catch (error: any) {
      toast.error(error.message || 'Failed to execute query');
      console.error('Query execution error:', error);
    } finally {
      setIsExecuting(false);
    }
  };

  const handleExecuteClick = () => {
    if (!sql) {
      toast.error('No query to execute');
      return;
    }
    setShowExecuteDialog(true);
  };

  const saveQuery = async () => {
    if (!sql || !id) return;

    try {
      await api.saveQuery({
        databaseId: id as string,
        name: `Visual Query ${new Date().toLocaleString()}`,
        query: sql,
        description: 'Created with visual query builder',
      });
      toast.success('Query saved successfully');
    } catch (error: any) {
      toast.error(error.message || 'Failed to save query');
    }
  };

  const visualTables = schema.map((table) => ({
    id: table.name,
    name: table.name,
    columns: (table.fields || []).map((field) => ({
      name: field.name,
      type: field.type,
      isPrimaryKey: field.isPrimary,
      isForeignKey: field.isForeign,
    })),
  }));

  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset>
        <header className="flex h-16 shrink-0 items-center gap-2 border-b">
          <div className="flex items-center gap-2 px-3">
            <SidebarTrigger />
            <Separator orientation="vertical" className="h-6" />
            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbPage>Visual Query Builder</BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
          </div>
          <div className="ml-auto flex items-center gap-2 px-3">
            {id && (
              <DatabaseSwitcher
                currentDatabaseId={id as string}
                onSwitch={(dbId) => navigate({ to: '/visual-query-builder/$id', params: { id: dbId } })}
              />
            )}
            <ModeToggle />
          </div>
        </header>

        {/* Main Content */}
        <div className="flex flex-1 flex-col gap-4 p-4 overflow-hidden">
        {/* Canvas */}
        <Card className="flex-1 overflow-hidden">
          <CardContent className="p-0 h-full">
            <VisualCanvas tables={visualTables} onQueryChange={setSql} />
          </CardContent>
        </Card>

        {/* SQL Preview & Actions */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <RiCodeLine className="size-4" />
                Generated SQL
              </CardTitle>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={saveQuery}
                  disabled={!sql}
                >
                  <RiSaveLine className="size-4 mr-2" />
                  Save Query
                </Button>
                <Button
                  size="sm"
                  onClick={handleExecuteClick}
                  disabled={!sql || isExecuting}
                >
                  <RiPlayLine className="size-4 mr-2" />
                  {isExecuting ? 'Executing...' : 'Execute Query'}
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="rounded-lg bg-muted p-4 font-mono text-sm max-h-32 overflow-auto">
              {sql || 'Drag tables to the canvas to start building your query...'}
            </div>
          </CardContent>
        </Card>

        {/* Results */}
        {result && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">
                  Results ({result.rows?.length || 0} rows)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="rounded-lg border overflow-auto max-h-64">
                  <pre className="p-4 text-xs">
                    {JSON.stringify(result.rows, null, 2)}
                  </pre>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}
        </div>

        {/* Execute Confirmation Dialog */}
        <AlertDialog open={showExecuteDialog} onOpenChange={setShowExecuteDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2">
                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-amber-500/10">
                  <RiAlertLine className="w-4 h-4 text-amber-500" />
                </div>
                Confirm Query Execution
              </AlertDialogTitle>
              <AlertDialogDescription className="space-y-3">
                <p>You are about to execute the following query on your database:</p>
                <div className="p-3 rounded-md bg-muted font-mono text-xs overflow-x-auto max-h-32 overflow-y-auto">
                  {sql}
                </div>
                <div className="flex items-start gap-2 p-3 rounded-md bg-amber-500/10 border border-amber-500/20">
                  <RiAlertLine className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-amber-900 dark:text-amber-200">
                    <strong>Warning:</strong> This operation will execute directly on your database.
                    Make sure you understand what this query does before proceeding.
                  </p>
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={executeQuery} className="bg-primary">
                Execute Query
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </SidebarInset>
    </SidebarProvider>
  );
}
